import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataType } from '@shared/helpers/enums/data-type';
import { GridColumn, ODataFilter } from '@shared/modules/grid/abstractions';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { asyncFilterOptionsFn } from '@shared/modules/prime-grid-filters/helpers/functions/async-filter-option';
import { KeyValue } from '@angular/common';
import { InvestmentJurisdictionService } from './investment-jurisdiction.service';
import { EntityTypeService } from '@core/services/entity-type/entity-type.service';
import { SecurityTypeService } from '@core/services/security-type/security-type.service';
import {
  Collection,
  EntityType,
  InvestmentJurisdiction,
  SecurityType,
} from '../models';
import { of } from 'rxjs';
import { transactionTypeItems } from '../constants/transaction-type-items';
import { DropdownItem } from '@shared/abstraction';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { TransactionTypeCellComponent } from '../components/transaction-type-cell/transaction-type-cell.component';

@Injectable()
export class TaxRulesManagerHeadersService {
  private readonly translateService = inject(TranslateService);
  private readonly entityTypeService = inject(EntityTypeService);
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );

  getHeaders(): GridColumn[] {
    return [
      {
        label: this.translateService.instant(
          'admin.grid.taxRules.columns.name'
        ),
        key: 'name',
        dataType: DataType.String,
        isSortable: true,
        sortKey: 'name',
        isFilterable: true,
        width: 250,
        filterableColumn: {
          type: FilterTypes.TextField,
          oDataQueryBuilder: (columnFilter): void => {
            columnFilter.updateQuery({
              contains: columnFilter.getValue<string>(),
            });
          },
        },
      },
      {
        label: 'Investment jurisdiction',
        key: 'investmentJurisdiction.name',
        dataType: DataType.String,
        isFilterable: true,
        isSortable: true,
        sortKey: 'investmentJurisdiction/country/name',
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'investmentJurisdiction/country/name',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.investmentJurisdictionService.activeInvestmentJurisdictionList(),
              ({ items }: Collection<InvestmentJurisdiction>) => {
                return items.map(
                  (investmentJurisdiction: InvestmentJurisdiction) => {
                    return {
                      value: investmentJurisdiction.investmentJurisdictionId
                        ? investmentJurisdiction.investmentJurisdictionId.toString()
                        : '',
                      key: investmentJurisdiction.name
                        ? investmentJurisdiction.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: 'Entity type',
        key: 'entityType.name',
        dataType: DataType.String,
        isFilterable: true,
        isSortable: true,
        sortKey: 'entityType/name',
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'entityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.entityTypeService.get(),
              ({ items }: Collection<EntityType>) => {
                return items.map((entityType: EntityType) => {
                  return {
                    value: entityType.entityTypeId.toString()
                      ? entityType.entityTypeId
                      : '',
                    key: entityType.name ? entityType.name.toString() : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: 'Security type',
        key: 'securityType.name',
        dataType: DataType.String,
        isFilterable: true,
        isSortable: true,
        sortKey: 'securityType/name',
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'securityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.securityTypeService.activeSecurityTypeList(),
              ({ items }: Collection<SecurityType>) => {
                return items.map((securityType: SecurityType) => {
                  return {
                    value: securityType.securityTypeId
                      ? securityType.securityTypeId.toString()
                      : '',
                    key: securityType.name ? securityType.name.toString() : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant('taxPolicy.transactionType'),
        key: 'generalRate',
        dataType: DataType.Template,
        templateCell: TransactionTypeCellComponent,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'generalRate',
          options: {
            items: of(
              transactionTypeItems
                .filter(
                  (item: DropdownItem) => item.value !== TransactionType.All
                )
                .map((item: DropdownItem) => {
                  return { value: item.value, key: item.label } as KeyValue<
                    string,
                    string
                  >;
                })
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              eq:
                columnFilter.getValue<DropdownItem[]>()[0].value ===
                TransactionType.GeneralRates,
            });
          },
        },
      },
    ];
  }
}
